import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Terminal, ChevronRight } from "lucide-react";
import { COMMAND_CATEGORIES } from "../data/commandCatalog";
import { WaChatFrame, WaBubbleSent, WaText } from "./WhatsAppMockup";
import { useBranding } from "@/hooks/useBranding";

function slugify(cmd) {
  return cmd
    .toLowerCase()
    .replace(/[!#]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const COLOR_MAP = {
  crimson: { text: "text-crimson", bg: "bg-crimson/10", border: "border-crimson/40", activeBg: "bg-crimson/15" },
  gold: { text: "text-gold", bg: "bg-gold/10", border: "border-gold/40", activeBg: "bg-gold/15" },
  blue: { text: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-400/40", activeBg: "bg-blue-500/15" },
};

export default function CommandCatalog() {
  const brandName = useBranding();
  const [activeCatId, setActiveCatId] = useState(COMMAND_CATEGORIES[0].id);
  const activeCat = useMemo(() => COMMAND_CATEGORIES.find((c) => c.id === activeCatId), [activeCatId]);
  const [activeCmd, setActiveCmd] = useState(COMMAND_CATEGORIES[0].commands[0]);

  const selectCategory = (cat) => {
    setActiveCatId(cat.id);
    setActiveCmd(cat.commands[0]);
  };

  const colors = COLOR_MAP[activeCat.color] || COLOR_MAP.gold;

  return (
    <section id="commands" className="max-w-7xl mx-auto px-5 md:px-8 py-20" data-testid="command-catalog-section">
      <div className="text-center max-w-2xl mx-auto mb-3">
        <span className="overline text-gold">La biblioteca más completa en WhatsApp</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Centro Interactivo del Catálogo de Comandos</h2>
        <p className="text-muted-foreground mt-3">
          Toca cualquier comando y mira exactamente lo que tu clan recibiría en el chat, en vivo.
        </p>
      </div>
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-sm font-bold text-gold">
          <Terminal size={15} /> +70 comandos especializados de guerra, ligas, capital y estrategia
        </span>
      </div>

      {/* Tabs de categoría - scroll horizontal en móvil, fila completa en desktop */}
      <div className="flex md:justify-center gap-2 overflow-x-auto no-scrollbar pb-2 mb-6 -mx-5 px-5 md:mx-0 md:px-0 md:flex-wrap">
        {COMMAND_CATEGORIES.map((cat) => {
          const c = COLOR_MAP[cat.color] || COLOR_MAP.gold;
          const active = cat.id === activeCatId;
          return (
            <button
              key={cat.id}
              data-testid={`command-category-tab-${cat.id}`}
              onClick={() => selectCategory(cat)}
              className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold border transition-all flex items-center gap-1.5
                ${active ? `${c.activeBg} ${c.text} ${c.border}` : "border-white/10 text-muted-foreground hover:text-white hover:border-white/25"}`}
            >
              <span>{cat.emoji}</span> {cat.label}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Lista de comandos de la categoría activa */}
        <div className="lg:col-span-2 card-surface p-3 sm:p-4 max-h-[520px] overflow-y-auto scrollbar-thin" data-testid="command-list-panel">
          <div className={`text-xs font-bold uppercase tracking-wider mb-3 px-1 ${colors.text}`}>
            {activeCat.emoji} {activeCat.label} · {activeCat.commands.length} comandos
          </div>
          <div className="space-y-1.5">
            {activeCat.commands.map((c) => {
              const isActive = activeCmd.cmd === c.cmd;
              return (
                <button
                  key={c.cmd}
                  data-testid={`command-item-button-${slugify(c.cmd)}`}
                  onClick={() => setActiveCmd(c)}
                  className={`w-full text-left rounded-md px-3 py-2.5 transition-all flex items-start gap-2 group
                    ${isActive ? `${colors.bg} border ${colors.border}` : "border border-transparent hover:bg-white/5"}`}
                >
                  <ChevronRight size={14} className={`mt-0.5 shrink-0 transition-transform ${isActive ? `${colors.text} rotate-90` : "text-muted-foreground/50 group-hover:translate-x-0.5"}`} />
                  <div className="min-w-0">
                    <code className={`font-mono text-sm font-semibold ${isActive ? colors.text : "text-white"}`}>{c.cmd}</code>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{c.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Vista previa dinámica estilo WhatsApp */}
        <div className="lg:col-span-3 lg:sticky lg:top-24 self-start">
          <WaChatFrame contactName={brandName} subtitle="en línea · verificado ⚡">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCmd.cmd}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                className="space-y-2.5"
              >
                <WaBubbleSent text={activeCmd.cmd} testid="whatsapp-chat-command-text" />
                <div className="flex justify-start" data-testid="whatsapp-chat-response-content">
                  <div className="relative bubble-tail-received bg-wa-received text-wa-text rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[94%] text-xs sm:text-sm shadow-md leading-relaxed">
                    <WaText text={activeCmd.response} />
                    <div className="text-right text-[10px] text-white/40 mt-1.5">14:32</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </WaChatFrame>
          <p className="text-center text-xs text-muted-foreground mt-3">
            Ejemplo ilustrativo de respuesta. El bot real responde con los datos en vivo de tu clan.
          </p>
        </div>
      </div>
    </section>
  );
}
